
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCentralizedAuth } from '@/context/CentralizedAuthProvider';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import LoginForm from '@/components/auth/LoginForm';
import ErrorDisplay from '@/components/auth/ErrorDisplay';

const Login = () => {
  const { login, isAuthenticated, isLoading: authLoading } = useCentralizedAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Reference to track if component is mounted
  const isMounted = useRef(true);
  
  // Check if user is already authenticated and redirect if needed
  useEffect(() => {
    console.log("Login page mounted, auth state:", { isAuthenticated, isLoading: authLoading });
    
    if (isAuthenticated && !authLoading) {
      console.log("User is authenticated, redirecting from login page");
      const redirectPath = new URLSearchParams(location.search).get('redirect') || '/';
      console.log("Redirecting to:", redirectPath);
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, navigate, location.search, authLoading]);

  // Clean up when component mounts/unmounts
  useEffect(() => {
    console.log("Login component lifecycle: mount");
    isMounted.current = true;
    setLoading(false);
    setError('');
    
    return () => {
      console.log("Login component lifecycle: unmount");
      isMounted.current = false;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Login form submitted");
    
    if (loading || authLoading) {
      console.log("Login already in progress, ignoring submission");
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      if (!email.trim()) {
        setError('Email is required');
        setLoading(false);
        return;
      }
      
      if (!password.trim()) {
        setError('Password is required');
        setLoading(false);
        return;
      }
      
      console.log('Attempting login...');
      await login(email, password);
      
      if (isMounted.current) {
        toast.success('Login successful!');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      
      if (isMounted.current) {
        if (err.message) {
          if (err.message.includes('Email not confirmed')) {
            setError('Please verify your email before logging in. Check your inbox for a confirmation link.');
          } else if (err.message.includes('Invalid login credentials')) {
            setError('Invalid email or password. Please try again.');
          } else {
            setError(err.message || 'An unexpected error occurred. Please try again.');
          }
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const isLoadingState = loading || authLoading;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <main className="flex-grow flex items-center justify-center p-4 sm:p-6 md:p-8">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-soft p-8 animate-fade-in">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-brand-blue mb-2">Welcome Back</h1>
            <p className="text-gray-600">Log in to continue your career journey</p>
          </div>
          
          <ErrorDisplay error={error} />
          
          <LoginForm 
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            isLoading={isLoadingState}
            onSubmit={handleSubmit}
          />
          
          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm">
              Don't have an account?{' '}
              <Link to="/signup" className="text-brand-lightBlue hover:underline font-medium">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Login;
